import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../api/client';
import { translateErrorCode } from '../../i18n';

/**
 * The bits every console section repeats: the error line, the save row, and
 * the "saved" acknowledgement.
 *
 * Extracted because five sections wanting the same three elements is exactly
 * the case where copying produces four subtly different versions -- one that
 * forgets `role="alert"`, one whose button stays enabled while the request is
 * in flight. A settings form gives no other sign that anything happened, so
 * these are the whole feedback surface.
 */

export function MutationError({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <p className="form-error" role="alert">
      {describeError(error)}
    </p>
  );
}

/**
 * A readable sentence for anything that can be thrown.
 *
 * `String(error)` was the fallback, and on a plain object -- which is what a
 * rejected fetch or a non-Error throw often is -- that renders the literal
 * "[object Object]" into the alert. An unhelpful message is bad; one that looks
 * like a bug in the product is worse.
 */
function describeError(error: unknown): string {
  if (error instanceof ApiError) return translateErrorCode(error.code, error.params);
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return translateErrorCode(undefined);
}

export function SaveRow({
  pending,
  succeeded,
  disabled,
  label,
  children,
}: {
  pending: boolean;
  succeeded: boolean;
  disabled?: boolean;
  /** Overrides the default "Save"; used by the buttons that are transitions. */
  label?: string;
  /** Extra controls that belong on the same row, such as a cancel. */
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="row-actions">
      <button
        type="submit"
        className="button button--primary"
        disabled={pending || disabled === true}
      >
        {pending ? t('editor.saving') : (label ?? t('editor.save'))}
      </button>
      {children}
      {succeeded && !pending ? (
        <span className="cell-ok" role="status">
          {t('settings.saved')}
        </span>
      ) : null}
    </div>
  );
}
